// Tutor-side types for AI Essay Review. Unlike the student app, the tutor sees
// AI rubric + suggestions. Provenance (model, rubricVersion) rides in rubric.meta.

export interface TutorEssayVersionSummary {
    id: number;
    versionNumber: number;
    label: string;
    wordCount: number;
    commentCount: number;                   // published (sent) comments only
    unsentCount: number;                    // private-draft comments not yet sent
    latestAnalysisStatus: string | null;   // queued|running|succeeded|failed
    latestOverallScore: number | null;
    submittedAt: string | null;            // student submitted this draft for review
    createdAt: string;
}

// Sibling drafts returned alongside a single version (the review rail).
export interface TutorEssayDraft {
    id: number;
    versionNumber: number;
    label: string;
    wordCount: number;
    commentCount: number;                   // published (sent) comments only
    unsentCount: number;                    // private-draft comments not yet sent
    submittedAt: string | null;
    latestOverallScore: number | null;
    createdAt: string;
}

export interface TutorEssaySummary {
    id: number;
    title: string;
    prompt: string;
    essayType: 'personal' | 'supplement';
    collegeId: string | null;
    collegeName: string | null;
    wordLimit: number | null;
    createdAt: string;
    modifiedAt: string;
    versions: TutorEssayVersionSummary[];
}

export interface RubricCategory {
    score: number;
    rationale: string;
}

// Countable evidence (rubric v6+). Items whose quote could not be re-located in the
// essay keep their place in the list (the count is the signal) but carry grounded=false
// and null offsets — render those without a jump-to-text affordance.
export interface EvidenceValue {
    value: string;
    anchorText: string;
    grounded: boolean;
    anchorStart: number | null;
    anchorEnd: number | null;
    shown: boolean;           // demonstrated through action/detail vs merely asserted
}

export interface EvidenceInsight {
    summary: string;
    anchorText: string;
    grounded: boolean;
    anchorStart: number | null;
    anchorEnd: number | null;
    quality: 'predictable' | 'illuminating';
}

export interface EssayRubric {
    overallScore?: number;
    categories?: Record<string, RubricCategory>;
    generalReview?: string;   // tutor-only prose overall assessment (rubric v2+; absent on old analyses)
    // v6+ only. Absent = that analysis's rubric version did not collect evidence —
    // NOT "the essay has no values". Never render an empty state from absence.
    evidence?: {
        valuesNamed?: EvidenceValue[];
        insightMoments?: EvidenceInsight[];
    };
    meta?: {
        model?: string;
        rubricVersion?: string;
        tokens?: Record<string, number>;
        costUsd?: number;
    };
}

export interface EssaySuggestion {
    id: string;
    category: string;        // voice|structure|prompt_fit|mechanics
    severity: string;        // notice|suggested|worth_addressing
    anchorStart: number;
    anchorEnd: number;
    anchorText: string;      // '' on a general suggestion (sentinel, same as comments)
    // Whole-essay suggestion with no anchor (rubric v7+). Absent on older analyses —
    // treat absence as false.
    isGeneral?: boolean;
    body: string;
    status: string;          // pending|dismissed|published
    publishedCommentId: number | null;
}

export interface EssayAnalysis {
    id: number;
    status: string;          // queued|running|succeeded|failed
    rubric: EssayRubric;
    suggestions: EssaySuggestion[];
    errorMessage: string;
    model?: string | null;
    rubricVersion?: string | null;
    createdAt: string;
}

export interface TutorEssayComment {
    id: number;
    anchorStart: number;
    anchorEnd: number;
    anchorText: string;
    body: string;
    isGeneral: boolean;               // whole-essay comment (no anchor)
    isResolved: boolean;
    authorTutorId: number | null;
    authorName: string | null;
    isPublished: boolean;             // private draft until "Send to student"
    publishedAt: string | null;
    sourceSuggestionId: string | null;   // set when published from an AI suggestion
    createdAt: string;
    modifiedAt: string;
}

export interface TutorEssayVersionDetail {
    id: number;
    essayId: number;
    versionNumber: number;
    label: string;
    content: string;
    wordCount: number;
    submittedAt: string | null;
    createdAt: string;
    modifiedAt: string;
}

// A prior-reviewed comment as the student saw it (student-style serialization:
// published only, no publish/AI-provenance fields).
export interface PriorReviewedComment {
    id: number;
    anchorStart: number;
    anchorEnd: number;
    anchorText: string;
    body: string;
    isGeneral: boolean;
    isResolved: boolean;
    authorTutorId: number | null;
    authorName: string | null;
    createdAt: string;
    modifiedAt: string;
}

// The nearest earlier draft with sent comments — powers the compare view.
export interface PriorReviewedDraft {
    id: number;
    versionNumber: number;
    label: string;
    content: string;
    comments: PriorReviewedComment[];
}

export const CATEGORY_ORDER = ['voice', 'detail', 'writing', 'character', 'reflection', 'purpose'];

export const CATEGORY_LABELS: Record<string, string> = {
    voice: 'Voice', detail: 'Detail', writing: 'Writing',
    character: 'Character', reflection: 'Reflection', purpose: 'Purpose',
};

export const SUGGESTION_CATEGORY_LABELS: Record<string, string> = {
    voice: 'Voice', structure: 'Structure', prompt_fit: 'Prompt-fit', mechanics: 'Mechanics',
};

export const SEVERITY_LABELS: Record<string, string> = {
    notice: 'Notice', suggested: 'Suggested', worth_addressing: 'Worth addressing',
};

export function essayWordCount(text: string): number {
    return (text || '').trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

// The official Common App personal-statement prompts (2024–25 / 2025–26). Used only
// to render the "PROMPT N" tag — students pick one and we store the full prompt text.
export const COMMON_APP_PROMPTS: string[] = [
    'Some students have a background, identity, interest, or talent so meaningful they believe their application would be incomplete without it. If this sounds like you, share your story.',
    'The lessons we take from obstacles can be fundamental to later success. Recount a time you faced a challenge, setback, or failure. How did it affect you, and what did you learn?',
    'Reflect on a time you questioned or challenged a belief or idea. What prompted your thinking? What was the outcome?',
    'Reflect on something someone has done for you that made you happy or thankful in a surprising way. How has this gratitude affected or motivated you?',
    'Discuss an accomplishment, event, or realization that sparked a period of personal growth and a new understanding of yourself or others.',
    'Describe a topic, idea, or concept you find so engaging it makes you lose all track of time. Why does it captivate you? What or who do you turn to to learn more?',
    "Share an essay on any topic of your choice — one you've already written, one that responds to a different prompt, or one of your own design.",
];

export function promptNumber(prompt: string): number | null {
    const i = COMMON_APP_PROMPTS.indexOf((prompt || '').trim());
    return i === -1 ? null : i + 1;
}

export function scoreLabel(score: number | null | undefined): string {
    return score != null ? score.toFixed(1) : '—';
}
